import { Badge } from '@crowdfpl/ui-web/badge';
import { Button } from '@crowdfpl/ui-web/button';
import { Input } from '@crowdfpl/ui-web/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@crowdfpl/ui-web/select';
import { Sheet, SheetContent, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/helpers/utils';
import { usePlayersSearch } from '@/hooks/data/players';
import { useCurrentSeason } from '@/hooks/data/seasons';
import { useTeams } from '@/hooks/data/teams';

export interface MobilePlayerValue {
  id: string;
  fpl_id: number;
  web_name: string;
  element_type?: number;
  team?: { name: string; short_name: string; id?: string };
  now_cost?: number;
  selected_by_percent?: string;
}

interface ComboboxMobileProps {
  id: string;
  placeholder: string;
  value: MobilePlayerValue | null;
  onChange: (player: MobilePlayerValue | null) => void;
  excludePlayer?: MobilePlayerValue | null;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  customTitle?: string;
}

function getPositionLabel(elementType?: number): string {
  switch (elementType) {
    case 1: return 'GK';
    case 2: return 'DEF';
    case 3: return 'MID';
    case 4: return 'FWD';
    default: return '';
  }
}

function getPositionValue(position: string): number | undefined {
  switch (position) {
    case 'GK': return 1;
    case 'DEF': return 2;
    case 'MID': return 3;
    case 'FWD': return 4;
    default: return undefined;
  }
}

const LIMIT = 40;

export function ComboboxMobile({ id, placeholder, value, onChange, excludePlayer, className, open: controlledOpen, onOpenChange, customTitle }: ComboboxMobileProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? (onOpenChange || (() => { })) : setInternalOpen;
  const sheetRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [allPlayers, setAllPlayers] = useState<MobilePlayerValue[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Get season for teams
  const { data: seasonData } = useCurrentSeason();
  const { data: teamsData } = useTeams({ season_id: seasonData?.id, limit: 100 });

  // Convert position string to number
  const positionNumber = selectedPosition ? getPositionValue(selectedPosition) : undefined;

  // Track if we should load more (only when user scrolls to bottom)
  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Fetch players with current filters
  // Always fetch when sheet is open, sorted by selected_by_percent when no search query
  const { data: searchData, isLoading, isFetching } = usePlayersSearch({
    query: debouncedSearchQuery,
    position: positionNumber,
    team: selectedTeamId || undefined,
    limit: LIMIT,
    offset: 0, // Always fetch from start, we'll accumulate in state
    enabled: isOpen, // Fetch immediately when sheet opens
    sort: debouncedSearchQuery ? 'total_points.desc' : 'selected_by_percent.desc',
  });

  // Reset and accumulate players when filters change or when sheet opens
  useEffect(() => {
    if (searchData?.players && isOpen) {
      setAllPlayers(searchData.players);
      setOffset(LIMIT);
      setShouldLoadMore(false); // Reset load more flag
    }
  }, [searchData?.players, debouncedSearchQuery, selectedPosition, selectedTeamId, isOpen]);

  // Load more players - only when shouldLoadMore is true
  const { data: loadMoreData } = usePlayersSearch({
    query: debouncedSearchQuery,
    position: positionNumber,
    team: selectedTeamId || undefined,
    limit: LIMIT,
    offset: offset,
    enabled: shouldLoadMore && offset > 0 && !isLoading && (searchData?.pagination.hasMore ?? false),
    sort: debouncedSearchQuery ? 'total_points.desc' : 'selected_by_percent.desc',
  });

  // Accumulate more players when loadMoreData arrives
  useEffect(() => {
    if (loadMoreData?.players && loadMoreData.players.length > 0) {
      setAllPlayers(prev => {
        // Filter out duplicates by id
        const existingIds = new Set(prev.map(p => p.id));
        const newPlayers = loadMoreData.players.filter(p => !existingIds.has(p.id));
        return [...prev, ...newPlayers];
      });
      setShouldLoadMore(false); // Reset flag after loading
      if (loadMoreData.pagination.hasMore) {
        setOffset(prev => prev + LIMIT);
      } else {
        setOffset(0); // No more pages
      }
    }
  }, [loadMoreData]);

  // Filter out excluded player and ensure no duplicates by id
  // Define filteredPlayers before it's used in useEffect dependencies
  const filteredPlayers = allPlayers
    .filter((p, index, self) => self.findIndex(pl => pl.id === p.id) === index) // Remove duplicates by id
    .filter((p) => (excludePlayer ? p.fpl_id !== excludePlayer.fpl_id : true));

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!isOpen || !searchData) return;

    let observer: IntersectionObserver | null = null;

    // Wait for DOM to be ready
    const timeoutId = setTimeout(() => {
      if (!loadMoreRef.current) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !isFetching && !isLoading && (searchData?.pagination.hasMore ?? false) && !shouldLoadMore) {
            // Only trigger load more when user scrolls to bottom
            setShouldLoadMore(true);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px', // Trigger earlier to preload
          root: scrollContainerRef.current, // Use scroll container as root
        },
      );

      observer.observe(loadMoreRef.current);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [isOpen, isFetching, isLoading, searchData?.pagination.hasMore, searchData, shouldLoadMore, filteredPlayers.length]);

  const handlePlayerSelect = (player: MobilePlayerValue) => {
    onChange(player);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedPosition('');
    setSelectedTeamId('');
    setOffset(0);
    setAllPlayers([]);
    if (inputRef.current) inputRef.current.blur();
  };

  // Sync internal state when controlled open changes
  useEffect(() => {
    if (isControlled && controlledOpen === false) {
      setSearchQuery('');
      setSelectedPosition('');
      setSelectedTeamId('');
      setOffset(0);
      setAllPlayers([]);
    }
  }, [isControlled, controlledOpen]);

  // Reset filters when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedPosition('');
      setSelectedTeamId('');
      setOffset(0);
      setAllPlayers([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timeoutId = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const handleFilterChange = (type: 'position' | 'team', value: string) => {
    if (type === 'position') {
      setSelectedPosition(value);
    } else {
      setSelectedTeamId(value);
    }
    setOffset(0);
    setAllPlayers([]);
  };

  const clearFilters = () => {
    setSelectedPosition('');
    setSelectedTeamId('');
    setOffset(0);
    setAllPlayers([]);
  };

  const hasActiveFilters = !!selectedPosition || !!selectedTeamId;

  const headerHeight = 240; // header + input + filters + badges
  const contentHeight = `calc(100dvh - ${headerHeight}px)`;


  return (
    <Sheet open={isOpen} onOpenChange={(open) => {
      // Don't close if Select dropdown is open
      if (!open) {
        const activeElement = document.activeElement;
        const isSelectOpen = activeElement?.closest('[role="listbox"]') ||
          activeElement?.closest('[data-radix-portal]') ||
          document.querySelector('[data-radix-select-content]');
        if (isSelectOpen) {
          // Keep sheet open if Select is active
          return;
        }
      }
      setIsOpen(open);
    }}>
      <Button
        type="button"
        variant="outline"
        className={cn('h-9 justify-between text-left font-normal flex-1 min-w-0 relative', !value && 'text-muted-foreground', className)}
        onClick={() => setIsOpen(true)}
      >
        {!value && (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        )}
        <span className={cn(!value ? 'pl-4' : 'truncate')}>{value ? value.web_name : placeholder}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>

      <SheetContent
        ref={sheetRef}
        side="bottom"
        className="h-dvh p-0 flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex-shrink-0 border-b bg-background z-10">
          <div className="flex items-center justify-between p-4">
            <SheetTitle className="text-lg font-semibold">
              {customTitle || `Select ${id === 'playerA' ? 'Player A' : 'Player B'}`}
            </SheetTitle>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Search input with icon */}
          <div className="px-4 pb-3">
            <div className="relative">
              {!searchQuery && (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              )}
              <Input
                ref={inputRef}
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSearchQuery(newValue);
                  // Clear selected value only if input is completely empty
                  if (value && newValue.trim() === '') {
                    onChange(null);
                    // Also clear the debounced query to show all players
                    setDebouncedSearchQuery('');
                  }
                }}
                className={cn('h-10', !searchQuery && 'pl-9')}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Filters row */}
          <div className="px-4 pb-3 grid grid-cols-2 gap-2">
            <Select value={selectedPosition} onValueChange={(v) => handleFilterChange('position', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GK">GK</SelectItem>
                <SelectItem value="DEF">DEF</SelectItem>
                <SelectItem value="MID">MID</SelectItem>
                <SelectItem value="FWD">FWD</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedTeamId} onValueChange={(v) => handleFilterChange('team', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                {teamsData?.teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.short_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Active filters badges */}
          {hasActiveFilters && (
            <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
              {selectedPosition && (
                <Badge variant="secondary" className="gap-1 text-xs h-6">
                  {selectedPosition}
                  <X
                    className="h-2 w-2 cursor-pointer"
                    onClick={() => handleFilterChange('position', '')}
                  />
                </Badge>
              )}
              {selectedTeamId && (
                <Badge variant="secondary" className="gap-1 text-xs h-6">
                  {teamsData?.teams.find(t => t.id === selectedTeamId)?.short_name || 'Team'}
                  <X
                    className="h-2 w-2 cursor-pointer"
                    onClick={() => handleFilterChange('team', '')}
                  />
                </Badge>
              )}
              <button
                onClick={clearFilters}
                className="h-6 px-2 text-xs shrink-0 text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden" style={{ height: contentHeight }}>
          <div
            ref={scrollContainerRef}
            className="h-full overflow-y-auto px-4"
            onWheel={(e) => {
              // Prevent page scroll when scrolling inside list
              e.stopPropagation();
              const target = e.currentTarget;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const isAtTop = scrollTop === 0;
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

              // Prevent scroll if at boundaries
              if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                e.preventDefault();
              }
            }}
            onTouchMove={(e) => {
              // Prevent page scroll on mobile
              e.stopPropagation();
            }}
          >
            <div className="space-y-1 pb-4">
              {isLoading && offset === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">Loading...</div>
              ) : filteredPlayers.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">No players found</div>
              ) : (
                <>
                  {filteredPlayers.map((p) => {
                    const isExcluded = excludePlayer?.fpl_id === p.fpl_id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => !isExcluded && handlePlayerSelect({
                          id: p.id,
                          fpl_id: p.fpl_id,
                          web_name: p.web_name,
                          element_type: p.element_type,
                          team: p.team,
                          now_cost: p.now_cost,
                          selected_by_percent: p.selected_by_percent,
                        })}
                        disabled={isExcluded}
                        className={cn(
                          'w-full text-left p-2 rounded-md hover:bg-primary/10 hover:border-primary/50 transition-colors border border-transparent',
                          'flex items-center justify-between',
                          isExcluded && 'opacity-50 cursor-not-allowed',
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono shrink-0">
                            {getPositionLabel(p.element_type)}
                          </span>
                          <span className="font-medium truncate">{p.web_name}</span>
                          <span className="text-sm text-muted-foreground truncate shrink-0">
                            {p.team?.short_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                          {typeof p.now_cost === 'number' && (
                            <span>£{((p.now_cost ?? 0) / 10).toFixed(1)}m</span>
                          )}
                          {p.selected_by_percent && (
                            <span>{p.selected_by_percent}%</span>
                          )}
                        </div>
                      </button>
                    );
                  })}

                  {/* Load more trigger - always render when there's more data */}
                  {searchData?.pagination.hasMore && (
                    <div ref={loadMoreRef} className="py-4 text-center min-h-[40px]">
                      {isFetching && (
                        <div className="text-sm text-muted-foreground">Loading more...</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ComboboxMobile;
